import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Alert } from '@/components/common/Alert';
import { Badge } from '@/components/common/Badge';
import { orderService } from '@/services/order.service';
import { productService } from '@/services/product.service';
import { invoiceService } from '@/services/invoice.service';
import { Order, OrderItem, OrderAccessTokenResponse } from '@/types/order.types';
import { Product } from '@/types/product.types';
import { Invoice } from '@/types/invoice.types';
import { formatPrice } from '@/utils/formatters';
import { parseOrderItems } from '@/utils/order.utils';
import '@/styles/dashboard.css';
import {
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  XCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  PlusIcon,
  EyeIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

export const Commandes = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const isProfessionalOrdersPage = location.pathname === '/admin/commandes-professionnelles';
  const forcedManualCustomerType: 'particulier' | 'professionnel' = isProfessionalOrdersPage ? 'professionnel' : 'particulier';
  const professionalStatuses = ['devis', 'validee', 'facture_emise', 'paiement_differe', 'livree', 'annulee'] as const;
  const statusLabelMap: Record<string, string> = {
    pending: t('adminOrders.pending'),
    processing: t('adminOrders.processing'),
    shipped: t('adminOrders.shipped'),
    delivered: t('adminOrders.delivered'),
    completed: t('adminOrders.completed'),
    cancelled: t('adminOrders.cancelled'),
    devis: t('adminOrders.devis'),
    validee: t('adminOrders.validee'),
    facture_emise: t('adminOrders.facture_emise'),
    paiement_differe: t('adminOrders.paiement_differe'),
    livree: t('adminOrders.livree'),
    annulee: t('adminOrders.annulee'),
  };
  
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);
  const [activeTab, setActiveTab] = useState<string>(statusParam || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState<string>('');
  const [filterOrderStatus, setFilterOrderStatus] = useState<string>('');
  const [filterCustomerType, setFilterCustomerType] = useState<string>(forcedManualCustomerType);
  const [bulkActionModalOpen, setBulkActionModalOpen] = useState(false);
  const [bulkSelectedStatus, setBulkSelectedStatus] = useState<string>('');
  const [updatingBulk, setUpdatingBulk] = useState(false);
  const [updatingOrderIds, setUpdatingOrderIds] = useState<Set<number>>(new Set());
  const [creatingBulkInvoices, setCreatingBulkInvoices] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState<Order | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [invoicesMap, setInvoicesMap] = useState<Record<number, Invoice>>({});
  const [creatingInvoice, setCreatingInvoice] = useState<number | null>(null);
  const [statusModalOpen, setStatusModalOpen] = useState<{ orderId: number; currentStatus: string } | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const [manualOrderModalOpen, setManualOrderModalOpen] = useState(false);
  const [manualOrderSubmitting, setManualOrderSubmitting] = useState(false);
  const [manualOrderErrors, setManualOrderErrors] = useState<Record<string, string>>({});
  const [manualProducts, setManualProducts] = useState<Product[]>([]);
  const [manualProductsLoading, setManualProductsLoading] = useState(false);
  const [manualProductsError, setManualProductsError] = useState<string | null>(null);

  const [orderAccessMap, setOrderAccessMap] = useState<Record<number, { data?: OrderAccessTokenResponse; loading: boolean; error?: string }>>({});
  
  // Autocomplete states
  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [cityNameSuggestions, setCityNameSuggestions] = useState<string[]>([]);
  const [postalSuggestions, setPostalSuggestions] = useState<string[]>([]);
  const [cityLookupStatus, setCityLookupStatus] = useState<'idle' | 'loading' | 'not_found'>('idle');
  const [cityNameLookupStatus, setCityNameLookupStatus] = useState<'idle' | 'loading' | 'not_found'>('idle');
  const [postalLookupStatus, setPostalLookupStatus] = useState<'idle' | 'loading' | 'not_found'>('idle');
  const autoFillGuardRef = useRef<{ field: 'customer_city' | 'customer_postal_code'; value: string } | null>(null);

  const [manualOrderForm, setManualOrderForm] = useState({
    customer_type: forcedManualCustomerType as 'particulier' | 'professionnel',
    company_name: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    customer_city: '',
    customer_postal_code: '',
    shipping_cost: 0,
    tax_rate: 0,
    tax_amount: 0,
    items: [
      {
        product_id: 0,
        product_name: '',
        quantity: 1,
        price: 0,
        total: 0,
      } as OrderItem,
    ],
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    delivered: 0,
    cancelled: 0,
    inProgress: 0, // Commandes en cours (processing + shipped)
  });

  // Mettre à jour l'onglet actif si un paramètre de statut est présent dans l'URL
  useEffect(() => {
    if (statusParam) {
      setActiveTab(statusParam);
    }
  }, [statusParam]);

  useEffect(() => {
    setFilterCustomerType(forcedManualCustomerType);
    if (isProfessionalOrdersPage) {
      if (activeTab === 'all') return;
      if (!professionalStatuses.includes(activeTab as any)) {
        setActiveTab('all');
      }
    }
  }, [isProfessionalOrdersPage, forcedManualCustomerType]);

  useEffect(() => {
    loadOrders();
    loadStats();
  }, [activeTab, filterDateFrom, filterDateTo, filterPaymentStatus, filterOrderStatus, filterCustomerType]);

  useEffect(() => {
    if (orders.length > 0) {
      loadInvoicesForOrders();
    }
  }, [orders]);

  useEffect(() => {
    const loadProducts = async () => {
      setManualProductsLoading(true);
      setManualProductsError(null);
      try {
        const products = await productService.getAll();
        setManualProducts(products);
      } catch (err) {
        console.error('Erreur lors du chargement des produits:', err);
        setManualProductsError(t('adminOrders.productsLoadError'));
      } finally {
        setManualProductsLoading(false);
      }
    };

    if (manualOrderModalOpen) {
      loadProducts();
    }
  }, [manualOrderModalOpen]);

  // Autocomplete effects
  useEffect(() => {
    const postalCode = manualOrderForm.customer_postal_code.trim();
    const isPostalAutoFill =
      autoFillGuardRef.current?.field === 'customer_postal_code' &&
      autoFillGuardRef.current?.value === postalCode;

    if (isPostalAutoFill) {
      window.setTimeout(() => {
        autoFillGuardRef.current = null;
      }, 0);
      return;
    }

    if (!/^\d{5}$/.test(postalCode)) {
      setCitySuggestions([]);
      setCityLookupStatus('idle');
      setCityNameSuggestions([]);
      setCityNameLookupStatus('idle');
      return;
    }

    setCityLookupStatus('loading');
    setCitySuggestions([]);
    setCityNameSuggestions([]);
    setCityNameLookupStatus('idle');

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `https://geo.api.gouv.fr/communes?codePostal=${postalCode}&fields=nom,codesPostaux&format=json`
        );
        const data = await response.json();
        const cities = Array.from(new Set((data || []).map((item: any) => item.nom))).filter(Boolean) as string[];

        if (cities.length === 0) {
          setCityLookupStatus('not_found');
          setCitySuggestions([]);
          return;
        }

        setCityLookupStatus('idle');

        if (cities.length === 1) {
          autoFillGuardRef.current = { field: 'customer_city', value: cities[0] };
          setManualOrderForm(prev => ({ ...prev, customer_city: cities[0] }));
          setCitySuggestions([]);
        } else {
          setCitySuggestions(cities);
        }
      } catch (error) {
        console.error('Erreur lors de la recherche de ville:', error);
        setCityLookupStatus('not_found');
        setCitySuggestions([]);
      }
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [manualOrderForm.customer_postal_code]);

  useEffect(() => {
    const city = manualOrderForm.customer_city.trim();
    const isCityAutoFill =
      autoFillGuardRef.current?.field === 'customer_city' &&
      autoFillGuardRef.current?.value === city;

    if (isCityAutoFill) {
      window.setTimeout(() => {
        autoFillGuardRef.current = null;
      }, 0);
      return;
    }

    if (city.length < 2) {
      setPostalSuggestions([]);
      setPostalLookupStatus('idle');
      return;
    }

    setPostalLookupStatus('loading');
    setPostalSuggestions([]);

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(city)}&fields=nom,codesPostaux&boost=population&limit=8`
        );
        const data = await response.json();
        const matches = (data || []).filter((item: any) => item?.nom);
        const exactMatches = matches.filter(
          (item: any) => item.nom.toLowerCase() === city.toLowerCase()
        );
        const codes = Array.from(
          new Set((exactMatches.length > 0 ? exactMatches : matches)
            .flatMap((item: any) => item.codesPostaux || []))
        ).filter(Boolean) as string[];

        if (codes.length === 0) {
          setPostalLookupStatus('not_found');
          setPostalSuggestions([]);
          return;
        }

        setPostalLookupStatus('idle');

        if (codes.length === 1) {
          autoFillGuardRef.current = { field: 'customer_postal_code', value: codes[0] };
          setManualOrderForm(prev => ({ ...prev, customer_postal_code: codes[0] }));
          setPostalSuggestions([]);
        } else {
          setPostalSuggestions(codes);
        }
      } catch (error) {
        console.error('Erreur lors de la recherche de code postal:', error);
        setPostalLookupStatus('not_found');
        setPostalSuggestions([]);
      }
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [manualOrderForm.customer_city]);

  useEffect(() => {
    const city = manualOrderForm.customer_city.trim();
    const isCityAutoFill =
      autoFillGuardRef.current?.field === 'customer_city' &&
      autoFillGuardRef.current?.value === city;

    if (isCityAutoFill) {
      setCityNameSuggestions([]);
      setCityNameLookupStatus('idle');
      return;
    }

    if (city.length < 2) {
      setCityNameSuggestions([]);
      setCityNameLookupStatus('idle');
      return;
    }

    setCityNameLookupStatus('loading');
    setCityNameSuggestions([]);

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(city)}&fields=nom&boost=population&limit=8`
        );
        const data = await response.json();
        const cities = Array.from(new Set((data || []).map((item: any) => item.nom))).filter(Boolean) as string[];

        if (cities.length === 0) {
          setCityNameLookupStatus('not_found');
          setCityNameSuggestions([]);
          return;
        }

        setCityNameLookupStatus('idle');
        setCityNameSuggestions(cities);
      } catch (error) {
        console.error('Erreur lors de la recherche de ville (auto-complétion):', error);
        setCityNameLookupStatus('not_found');
        setCityNameSuggestions([]);
      }
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [manualOrderForm.customer_city]);

  const handleSelectCity = (city: string) => {
    setManualOrderForm(prev => ({ ...prev, customer_city: city }));
    setCitySuggestions([]);
    setCityNameSuggestions([]);
    setCityLookupStatus('idle');
    setCityNameLookupStatus('idle');
    setPostalSuggestions([]);
    setPostalLookupStatus('idle');
  };

  const handleSelectPostalCode = (postalCode: string) => {
    autoFillGuardRef.current = { field: 'customer_postal_code', value: postalCode };
    setManualOrderForm(prev => ({ ...prev, customer_postal_code: postalCode }));
    setPostalSuggestions([]);
    setPostalLookupStatus('idle');
    setCitySuggestions([]);
    setCityLookupStatus('idle');
  };

  useEffect(() => {
    setManualOrderForm(prev => {
      if (prev.customer_type === forcedManualCustomerType) return prev;
      return {
        ...prev,
        customer_type: forcedManualCustomerType,
        company_name: forcedManualCustomerType === 'professionnel' ? prev.company_name : '',
      };
    });
  }, [forcedManualCustomerType]);

  useEffect(() => {
    if (manualOrderForm.customer_type === 'particulier' && manualOrderForm.company_name) {
      setManualOrderForm(prev => ({ ...prev, company_name: '' }));
    }
  }, [manualOrderForm.customer_type]);

  useEffect(() => {
    const loadAccessToken = async () => {
      if (!detailModalOpen) return;
      setOrderAccessMap(prev => ({
        ...prev,
        [detailModalOpen.id]: { ...prev[detailModalOpen.id], loading: true, error: undefined },
      }));
      try {
        const data = await orderService.getAccessToken(detailModalOpen.id);
        setOrderAccessMap(prev => ({
          ...prev,
          [detailModalOpen.id]: { data, loading: false },
        }));
      } catch (err: any) {
        const message = err.response?.data?.error || err.message || t('adminOrders.qrCodeError');
        setOrderAccessMap(prev => ({
          ...prev,
          [detailModalOpen.id]: { ...prev[detailModalOpen.id], loading: false, error: message },
        }));
      }
    };

    loadAccessToken();
  }, [detailModalOpen]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      
      // Gérer le filtre de statut : priorité au filtre manuel, sinon utiliser l'onglet actif
      if (filterOrderStatus) {
        // Si un filtre de statut est sélectionné, l'utiliser
        filters.status = filterOrderStatus;
      } else if (activeTab !== 'all') {
        // Sinon, utiliser l'onglet actif
        if (!isProfessionalOrdersPage && activeTab === 'delivered') {
          // Pour les commandes terminées, on ne filtre pas côté serveur
          // On va filtrer côté client pour inclure 'completed' et 'delivered'
        } else {
          filters.status = activeTab;
        }
      }

      // Envoyer les filtres de date au backend pour optimiser les requêtes
      if (filterDateFrom) {
        // Format: YYYY-MM-DD HH:MM:SS pour MySQL DATETIME
        filters.date_from = filterDateFrom + ' 00:00:00';
      }
      if (filterDateTo) {
        // Ajouter la fin de journée pour inclure toute la journée
        filters.date_to = filterDateTo + ' 23:59:59';
      }
      if (filterPaymentStatus) {
        filters.payment_status = filterPaymentStatus;
      }
      if (filterCustomerType) {
        filters.customer_type = filterCustomerType;
      }

      const data = await orderService.getAll(filters);
      setOrders(data);
      setError(null);
    } catch (err: any) {
      console.error('Erreur lors du chargement des commandes:', err);
      setError(t('adminOrders.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      if (isProfessionalOrdersPage) {
        const proOrders = await orderService.getAll({ customer_type: 'professionnel' } as any);
        const inProgress = proOrders.filter(
          order => ['validee', 'facture_emise', 'paiement_differe'].includes(order.order_status || '')
        ).length;
        const delivered = proOrders.filter(order => order.order_status === 'livree').length;
        const cancelled = proOrders.filter(order => order.order_status === 'annulee').length;
        const pending = proOrders.filter(order => order.order_status === 'devis').length;
        setStats({
          total: proOrders.length,
          pending,
          delivered,
          cancelled,
          inProgress,
        });
        return;
      }
      const data = await orderService.getStats();
      // Calculer les commandes en cours (processing + shipped)
      const allOrders = await orderService.getAll();
      const inProgress = allOrders.filter(
        order => order.order_status === 'processing' || order.order_status === 'shipped'
      ).length;
      
      setStats({
        ...data,
        inProgress,
      });
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
    }
  };

  const loadInvoicesForOrders = async () => {
    try {
      const invoices: Record<number, Invoice> = {};
      // Charger les factures en parallèle pour améliorer les performances
      const invoicePromises = orders.map(async (order) => {
        try {
          const invoice = await invoiceService.getByOrderId(order.id);
          return { orderId: order.id, invoice };
        } catch (err) {
          console.error(`Erreur lors du chargement de la facture pour la commande ${order.id}:`, err);
          return { orderId: order.id, invoice: null };
        }
      });
      
      const results = await Promise.all(invoicePromises);
      results.forEach(({ orderId, invoice }) => {
        if (invoice) {
          invoices[orderId] = invoice;
        }
      });
      
      setInvoicesMap(invoices);
      console.log('Factures chargées:', Object.keys(invoices).length, 'facture(s) trouvée(s)');
    } catch (err) {
      console.error('Erreur lors du chargement des factures:', err);
    }
  };

  const handleCreateInvoice = async (orderId: number) => {
    try {
      setCreatingInvoice(orderId);
      const invoice = await invoiceService.createFromOrder(orderId);
      setInvoicesMap({ ...invoicesMap, [orderId]: invoice });
      // Rediriger vers la page factures pour voir la facture dans le tableau
      navigate('/admin/factures');
      setNotification({ 
        type: 'success', 
        message: t('adminOrders.invoiceCreatedSuccessMsg', { number: invoice.invoice_number }) 
      });
    } catch (err: any) {
      console.error('Erreur lors de la création de la facture:', err);
      const errorMessage = err.response?.data?.error || err.message || t('adminOrders.invoiceCreateError');
      setNotification({ type: 'error', message: errorMessage });
    } finally {
      setCreatingInvoice(null);
    }
  };

  // Fonction pour voir ou créer une facture (crée si elle n'existe pas, puis ouvre)
  const handleViewOrCreateInvoice = async (orderId: number) => {
    try {
      // Si la facture existe déjà, ouvrir directement
      if (invoicesMap[orderId]) {
        navigate(`/admin/factures/${invoicesMap[orderId].id}`);
        return;
      }

      // Sinon, créer la facture puis ouvrir
      setCreatingInvoice(orderId);
      const invoice = await invoiceService.createFromOrder(orderId);
      setInvoicesMap({ ...invoicesMap, [orderId]: invoice });
      navigate(`/admin/factures/${invoice.id}`);
    } catch (err: any) {
      console.error('Erreur lors de la création/ouverture de la facture:', err);
      const errorMessage = err.response?.data?.error || err.message || t('adminOrders.invoiceCreateError');
      setNotification({ type: 'error', message: errorMessage });
    } finally {
      setCreatingInvoice(null);
    }
  };

  const handleManualOrderFieldChange = (field: keyof typeof manualOrderForm, value: any) => {
    setManualOrderForm(prev => ({ ...prev, [field]: value }));
    if (manualOrderErrors[field as string]) {
      setManualOrderErrors(prev => {
        const updated = { ...prev };
        delete updated[field as string];
        return updated;
      });
    }
  };

  const updateManualOrderItem = (index: number, field: keyof OrderItem, value: any) => {
    setManualOrderForm(prev => {
      const items = [...prev.items];
      const item = { ...items[index] };
      if (field === 'quantity') {
        item.quantity = Math.max(1, parseInt(value, 10) || 1);
      } else if (field === 'price') {
        item.price = Math.max(0, parseFloat(value) || 0);
      } else if (field === 'product_name') {
        item.product_name = value;
      }
      item.total = (item.quantity || 0) * (item.price || 0);
      items[index] = item;
      return { ...prev, items };
    });
  };

  const handleManualProductSelect = (index: number, productId: number) => {
    const selectedProduct = manualProducts.find(product => product.id === productId);
    setManualOrderForm(prev => {
      const items = [...prev.items];
      const item = { ...items[index] };
      item.product_id = productId;
      item.product_name = selectedProduct?.name || '';
      const unitPrice = selectedProduct?.reduced_price ?? selectedProduct?.price ?? 0;
      item.price = unitPrice;
      item.total = (item.quantity || 0) * unitPrice;
      items[index] = item;
      return { ...prev, items };
    });
    if (manualOrderErrors[`item_${index}_product_name`]) {
      setManualOrderErrors(prev => {
        const updated = { ...prev };
        delete updated[`item_${index}_product_name`];
        return updated;
      });
    }
  };

  const addManualOrderItem = () => {
    setManualOrderForm(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product_id: 0,
          product_name: '',
          quantity: 1,
          price: 0,
          total: 0,
        } as OrderItem,
      ],
    }));
  };

  const removeManualOrderItem = (index: number) => {
    setManualOrderForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const resetManualOrderForm = () => {
    setManualOrderErrors({});
    setManualOrderForm({
      customer_type: forcedManualCustomerType,
      company_name: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      customer_address: '',
      customer_city: '',
      customer_postal_code: '',
    shipping_cost: 0,
    tax_rate: 0,
    tax_amount: 0,
    items: [
        {
          product_id: 0,
          product_name: '',
          quantity: 1,
          price: 0,
          total: 0,
        } as OrderItem,
      ],
    });
  };

  const handleCreateManualOrder = async () => {
    const customerType = forcedManualCustomerType;
    const errors: Record<string, string> = {};
    if (!manualOrderForm.customer_name.trim()) {
      errors.customer_name = t('adminOrders.customerNameRequired');
    }
    if (!manualOrderForm.customer_email.trim()) {
      errors.customer_email = t('adminOrders.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualOrderForm.customer_email)) {
      errors.customer_email = t('adminOrders.emailInvalid');
    }
    if (!manualOrderForm.customer_phone.trim()) {
      errors.customer_phone = t('adminOrders.phoneRequired');
    }
    if (!manualOrderForm.customer_address.trim()) {
      errors.customer_address = t('adminOrders.addressRequired');
    }
    if (!manualOrderForm.customer_city.trim()) {
      errors.customer_city = t('adminOrders.cityRequired');
    }
    if (!manualOrderForm.customer_postal_code.trim()) {
      errors.customer_postal_code = t('adminOrders.postalCodeRequired');
    }
    if (customerType === 'professionnel' && !manualOrderForm.company_name.trim()) {
      errors.company_name = t('adminOrders.companyNameRequired');
    }

    manualOrderForm.items.forEach((item, index) => {
      if (!item.product_id || item.product_id === 0) {
        errors[`item_${index}_product_name`] = t('adminOrders.productRequired');
      }
      if (!item.quantity || item.quantity < 1) {
        errors[`item_${index}_quantity`] = t('adminOrders.quantityInvalid');
      }
      if (item.price == null || item.price < 0) {
        errors[`item_${index}_price`] = t('adminOrders.priceInvalid');
      }
    });

    if (Object.keys(errors).length > 0) {
      setManualOrderErrors(errors);
      return;
    }

    const items = manualOrderForm.items.map(item => ({
      ...item,
      product_id: item.product_id || 0,
      total: (item.quantity || 0) * (item.price || 0),
    }));
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const shippingCost = Number(manualOrderForm.shipping_cost || 0);
    
    // Calcul de la TVA si un taux est défini
    const taxRate = Number(manualOrderForm.tax_rate || 0);
    let taxAmount = 0;
    
    if (taxRate > 0) {
      // Si TVA > 0, on considère que les prix unitaires sont HT ou TTC selon la logique métier
      // Ici on va assumer que l'utilisateur veut ajouter la TVA au sous-total
      taxAmount = (subtotal * taxRate) / 100;
    }
    
    const total = subtotal + shippingCost + taxAmount;

    setManualOrderSubmitting(true);
    try {
      await orderService.createManual({
        customer_name: manualOrderForm.customer_name.trim(),
        customer_email: manualOrderForm.customer_email.trim(),
        customer_phone: manualOrderForm.customer_phone.trim(),
        customer_address: manualOrderForm.customer_address.trim(),
        customer_city: manualOrderForm.customer_city.trim(),
        customer_postal_code: manualOrderForm.customer_postal_code.trim(),
        customer_type: customerType,
        company_name: customerType === 'professionnel'
          ? manualOrderForm.company_name.trim()
          : null,
        items,
        subtotal,
        shipping_cost: shippingCost,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total,
        payment_status: 'pending',
        order_status: 'pending',
      });
      setNotification({ type: 'success', message: t('adminOrders.manualOrderSuccess') });
      setManualOrderModalOpen(false);
      resetManualOrderForm();
      await loadOrders();
      await loadStats();
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || t('adminOrders.manualOrderError');
      setNotification({ type: 'error', message });
    } finally {
      setManualOrderSubmitting(false);
    }
  };

  const handleOpenStatusModal = (orderId: number, currentStatus: string) => {
    setStatusModalOpen({ orderId, currentStatus });
    setSelectedStatus(currentStatus);
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(null);
    setSelectedStatus('');
  };

  const handleUpdateStatus = async () => {
    if (!statusModalOpen || !selectedStatus) return;

    try {
      setUpdatingStatus(statusModalOpen.orderId);
      await orderService.update(statusModalOpen.orderId, { order_status: selectedStatus as any });
      await loadOrders();
      await loadStats();
      setNotification({ type: 'success', message: t('adminOrders.statusUpdateSuccess') });
      handleCloseStatusModal();
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour:', err);
      setNotification({ type: 'error', message: `${t('adminOrders.statusUpdateError')}: ${err.response?.data?.error || err.message}` });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async (id: number, orderNumber: string) => {
    if (window.confirm(t('adminOrders.deleteOrderConfirm', { orderNumber }))) {
      try {
        await orderService.delete(id);
        await loadOrders();
        await loadStats();
        alert(t('adminOrders.deleteSuccess'));
      } catch (err: any) {
        console.error('Erreur lors de la suppression:', err);
        alert(`${t('adminOrders.deleteError')}: ${err.response?.data?.error || err.message}`);
      }
    }
  };

  const handleGenerateAccessToken = async (orderId: number) => {
    setOrderAccessMap(prev => ({
      ...prev,
      [orderId]: { ...prev[orderId], loading: true, error: undefined },
    }));

    try {
      const data = await orderService.createAccessToken(orderId);
      setOrderAccessMap(prev => ({
        ...prev,
        [orderId]: { data, loading: false },
      }));
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || t('adminOrders.qrCodeError');
      setOrderAccessMap(prev => ({
        ...prev,
        [orderId]: { ...prev[orderId], loading: false, error: message },
      }));
    }
  };

  const buildOrderPrintHtml = (order: Order, items: OrderItem[], qrCodeDataUrl?: string | null) => {
    const rows = items.map(item => `
      <tr>
        <td>${item.product_name || t('adminOrders.product')}</td>
        <td class="center">${item.quantity || 0}</td>
        <td class="right">${formatPrice(item.price || 0)}</td>
        <td class="right">${formatPrice(item.total || 0)}</td>
      </tr>
    `).join('');

    const taxRow = order.tax_amount && order.tax_amount > 0
      ? `<tr><td>${t('adminOrders.tax', { rate: order.tax_rate || 0 })}</td><td class="right" colspan="3">${formatPrice(order.tax_amount)}</td></tr>`
      : '';

    const shippingRow = order.shipping_cost && order.shipping_cost > 0
      ? `<tr><td>${t('adminOrders.shipping')}</td><td class="right" colspan="3">${formatPrice(order.shipping_cost)}</td></tr>`
      : '';

    return `
      <html>
        <head>
          <title>${t('adminOrders.orderFormTitle')} ${order.order_number}</title>
          <style>
            @page { size: A4; margin: 12mm; }
            body { font-family: Arial, sans-serif; color: #111; padding: 0; font-size: 12px; }
            h1 { margin: 0 0 6px; font-size: 22px; }
            h2 { margin: 0 0 16px; font-size: 14px; font-weight: normal; color: #444; }
            .row { display: flex; justify-content: space-between; gap: 16px; }
            .box { border: 1px solid #ddd; border-radius: 8px; padding: 12px; flex: 1; }
            .box h3 { margin: 0 0 8px; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border-bottom: 1px solid #eee; padding: 6px; font-size: 12px; }
            th { text-align: left; background: #f7f7f7; }
            .right { text-align: right; }
            .center { text-align: center; }
            .totals { margin-top: 12px; }
            .totals td { border: none; }
            .qr { width: 120px; height: 120px; border: 1px solid #ddd; border-radius: 8px; padding: 6px; background: #fff; }
            .muted { color: #666; font-size: 12px; }
            table, tr, td, th { page-break-inside: avoid; }
            .box { page-break-inside: avoid; }
          </style>
        </head>
        <body>
          <div class="row" style="align-items:center;">
            <div>
              <h1>${t('adminOrders.orderFormTitle')}</h1>
              <h2>${t('adminOrders.orderLabel')} ${order.order_number}</h2>
            </div>
            ${qrCodeDataUrl ? `<img class="qr" src="${qrCodeDataUrl}" alt="${t('adminOrders.qrCodeAlt')}" />` : ''}
          </div>

          <div class="row" style="margin-top:16px;">
            <div class="box">
              <h3>${t('adminOrders.clientInfo')}</h3>
              <div><strong>${t('adminOrders.nameLabel')}</strong> ${order.customer_name}</div>
              <div><strong>${t('adminOrders.emailLabel')}</strong> ${order.customer_email}</div>
              <div><strong>${t('adminOrders.phoneLabel')}</strong> ${order.customer_phone || t('adminOrders.na')}</div>
              <div><strong>${t('adminOrders.addressLabel')}</strong> ${order.customer_address || t('adminOrders.na')}</div>
              <div><strong>${t('adminOrders.cityLabel')}</strong> ${order.customer_city || t('adminOrders.na')}</div>
              <div><strong>${t('adminOrders.postalCodeLabel')}</strong> ${order.customer_postal_code || t('adminOrders.na')}</div>
            </div>
            <div class="box">
              <h3>${t('adminOrders.statuses')}</h3>
              <div><strong>${t('adminOrders.orderStatus')}</strong> ${order.order_status}</div>
              <div><strong>${t('adminOrders.paymentStatus')}</strong> ${order.payment_status}</div>
              <div class="muted"><strong>${t('adminOrders.createdOn')}</strong> ${formatDate(order.created_at)}</div>
              ${order.updated_at ? `<div class="muted"><strong>${t('adminOrders.updatedOn')}</strong> ${formatDate(order.updated_at)}</div>` : ''}
            </div>
          </div>

          <div class="box" style="margin-top:16px;">
            <h3>${t('adminOrders.products')}</h3>
            <table>
              <thead>
                <tr>
                  <th>${t('adminOrders.product')}</th>
                  <th class="center">${t('adminOrders.quantity')}</th>
                  <th class="right">${t('adminOrders.unitPrice')}</th>
                  <th class="right">${t('adminOrders.total')}</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
            </table>

            <table class="totals">
              <tr><td>${t('adminOrders.subtotal')}</td><td class="right" colspan="3">${formatPrice(order.subtotal || 0)}</td></tr>
              ${shippingRow}
              ${taxRow}
              <tr><td><strong>${t('adminOrders.total')}</strong></td><td class="right" colspan="3"><strong>${formatPrice(order.total || 0)}</strong></td></tr>
            </table>
          </div>

          <script>
            (function() {
              const images = Array.from(document.images || []);
              if (images.length === 0) {
                setTimeout(() => window.print(), 300);
                window.onafterprint = () => window.close();
                return;
              }
              let loaded = 0;
              const done = () => {
                loaded += 1;
                if (loaded >= images.length) {
                  setTimeout(() => window.print(), 300);
                }
              };
              images.forEach(img => {
                if (img.complete) {
                  done();
                } else {
                  img.onload = done;
                  img.onerror = done;
                }
              });
              window.onafterprint = () => window.close();
            })();
          </script>
        </body>
      </html>
    `;
  };

  const handlePrintOrder = async (order: Order, items: OrderItem[]) => {
    let accessData = orderAccessMap[order.id]?.data;
    if (!accessData?.qr_code_data_url) {
      try {
        accessData = await orderService.getAccessToken(order.id);
        setOrderAccessMap(prev => ({
          ...prev,
          [order.id]: { data: accessData, loading: false },
        }));
      } catch (error) {
        await handleGenerateAccessToken(order.id);
        accessData = orderAccessMap[order.id]?.data;
      }
    }

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      return;
    }
    const html = buildOrderPrintHtml(order, items, accessData?.qr_code_data_url || null);
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
  };

  const handleBulkUpdateStatus = async () => {
    if (!bulkSelectedStatus || selectedOrders.length === 0) return;

    try {
      setUpdatingBulk(true);
      setUpdatingOrderIds(new Set(selectedOrders));
      
      // Utiliser Promise.all pour exécuter toutes les mises à jour en parallèle
      const updatePromises = selectedOrders.map(orderId =>
        orderService.update(orderId, { order_status: bulkSelectedStatus as any })
          .then(() => ({ orderId, success: true }))
          .catch((err) => {
            console.error(`Erreur lors de la mise à jour de la commande ${orderId}:`, err);
            return { orderId, success: false, error: err };
          })
      );
      
      const results = await Promise.all(updatePromises);
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      
      // Recharger les données après la mise à jour
      await loadOrders();
      await loadStats();
      
      // Afficher un message approprié selon les résultats
      if (failed === 0) {
        setNotification({ 
          type: 'success', 
          message: t('adminOrders.bulkUpdateSuccess', { count: successful }) 
        });
        // Réinitialiser la sélection et fermer le modal seulement si tout a réussi
        setSelectedOrders([]);
        setBulkActionModalOpen(false);
        setBulkSelectedStatus('');
      } else if (successful > 0) {
        setNotification({ 
          type: 'warning', 
          message: t('adminOrders.bulkUpdatePartial', { successful, failed }) 
        });
        // Retirer seulement les commandes mises à jour avec succès de la sélection
        const failedOrderIds = results.filter(r => !r.success).map(r => r.orderId);
        setSelectedOrders(prev => prev.filter(id => !failedOrderIds.includes(id)));
      } else {
        setNotification({ 
          type: 'error', 
          message: t('adminOrders.bulkUpdateNone') 
        });
      }
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour groupée:', err);
      setNotification({ 
        type: 'error', 
        message: `${t('adminOrders.bulkUpdateError')}: ${err.response?.data?.error || err.message || t('adminOrders.unexpectedError')}` 
      });
    } finally {
      setUpdatingBulk(false);
      setUpdatingOrderIds(new Set());
    }
  };

  // Fonction pour créer des factures en masse pour les commandes sélectionnées
  const handleBulkCreateInvoices = async () => {
    if (selectedOrders.length === 0) return;

    try {
      setCreatingBulkInvoices(true);
      
      // Filtrer les commandes qui n'ont pas encore de facture
      const ordersWithoutInvoice = selectedOrders.filter(orderId => !invoicesMap[orderId]);
      
      if (ordersWithoutInvoice.length === 0) {
        setNotification({ 
          type: 'info', 
          message: t('adminOrders.allHaveInvoice') 
        });
        return;
      }

      // Créer les factures en parallèle
      const createPromises = ordersWithoutInvoice.map(async (orderId) => {
        try {
          const invoice = await invoiceService.createFromOrder(orderId);
          return { orderId, invoice, success: true };
        } catch (error: any) {
          console.error(`Erreur lors de la création de la facture pour la commande ${orderId}:`, error);
          return { 
            orderId, 
            error: error.response?.data?.error || error.message || t('adminOrders.unknownError'), 
            success: false 
          };
        }
      });

      const results = await Promise.all(createPromises);
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      // Mettre à jour la map des factures
      const newInvoicesMap = { ...invoicesMap };
      successful.forEach(({ orderId, invoice }) => {
        newInvoicesMap[orderId] = invoice;
      });
      setInvoicesMap(newInvoicesMap);

      // Afficher les résultats
      if (successful.length > 0) {
        setNotification({ 
          type: 'success', 
          message: t('adminOrders.bulkInvoiceSuccess', { count: successful.length, failedPart: failed.length > 0 ? t('adminOrders.bulkInvoiceFailedPart', { count: failed.length }) : '' }) 
        });
      }

      if (failed.length > 0) {
        const failedDetails = failed.map(f => `Commande ${f.orderId}: ${f.error}`).join('\n');
        console.error('Échecs de création de factures:', failedDetails);
        if (successful.length === 0) {
          setNotification({ 
            type: 'error', 
            message: `${t('adminOrders.bulkInvoiceNone')} ${failed[0]?.error || t('adminOrders.unknownError')}` 
          });
        }
      }

      // Recharger les commandes pour mettre à jour l'affichage
      await loadOrders();
      
    } catch (err: any) {
      console.error('Erreur lors de la création groupée de factures:', err);
      setNotification({ 
        type: 'error', 
        message: `${t('adminOrders.bulkInvoiceError')}: ${err.response?.data?.error || err.message || t('adminOrders.unexpectedError')}` 
      });
    } finally {
      setCreatingBulkInvoices(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; icon: any; variant: 'success' | 'error' | 'warning' | 'info' | 'neutral' }> = {
      delivered: {
        label: t('adminOrders.delivered'),
        icon: CheckCircleIcon,
        variant: 'success',
      },
      completed: {
        label: t('adminOrders.completed'),
        icon: CheckCircleIcon,
        variant: 'success',
      },
      pending: {
        label: t('adminOrders.pending'),
        icon: ClockIcon,
        variant: 'warning',
      },
      processing: {
        label: t('adminOrders.processing'),
        icon: ClockIcon,
        variant: 'info',
      },
      shipped: {
        label: t('adminOrders.shipped'),
        icon: TruckIcon,
        variant: 'info',
      },
      cancelled: {
        label: t('adminOrders.cancelled'),
        icon: XCircleIcon,
        variant: 'error',
      },
      devis: {
        label: t('adminOrders.devis'),
        icon: DocumentTextIcon,
        variant: 'warning',
      },
      validee: {
        label: t('adminOrders.validee'),
        icon: CheckCircleIcon,
        variant: 'info',
      },
      facture_emise: {
        label: t('adminOrders.facture_emise'),
        icon: DocumentTextIcon,
        variant: 'info',
      },
      paiement_differe: {
        label: t('adminOrders.paiement_differe'),
        icon: ClockIcon,
        variant: 'warning',
      },
      livree: {
        label: t('adminOrders.livree'),
        icon: TruckIcon,
        variant: 'success',
      },
      annulee: {
        label: t('adminOrders.annulee'),
        icon: XCircleIcon,
        variant: 'error',
      },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
    <>

      <Badge variant={config.variant}>
        <Icon className="btn-icon" style={{ width: '14px', height: '14px' }} />
        {config.label}
      </Badge>
    
    </>
  );
  };

  const getPaymentBadge = (paymentStatus: string) => {
    const variant = paymentStatus === 'paid' ? 'success' : paymentStatus === 'pending' ? 'warning' : 'error';
    const label = paymentStatus === 'paid' ? t('adminOrders.paid') : paymentStatus === 'pending' ? t('adminOrders.pending') : t('adminOrders.unpaid');
    return (
    <>

      <Badge variant={variant}>
        • {label}
      </Badge>
    
    </>
  );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const professionalStatusTabs = [
    { key: 'all', label: t('adminOrders.allOrders') },
    { key: 'devis', label: t('adminOrders.devis') },
    { key: 'validee', label: t('adminOrders.validee') },
    { key: 'facture_emise', label: t('adminOrders.facture_emise') },
    { key: 'paiement_differe', label: t('adminOrders.paiement_differe') },
    { key: 'livree', label: t('adminOrders.livree') },
    { key: 'annulee', label: t('adminOrders.annulee') },
  ];

  const handleQuickStatusUpdate = async (orderId: number, nextStatus: string, message: string) => {
    try {
      setUpdatingOrderIds(prev => new Set(prev).add(orderId));
      await orderService.update(orderId, { order_status: nextStatus as any });
      setNotification({ type: 'success', message });
      await loadOrders();
      await loadStats();
    } catch (err: any) {
      setNotification({
        type: 'error',
        message: err.response?.data?.error || err.message || t('adminOrders.statusUpdateErrorMsg'),
      });
    } finally {
      setUpdatingOrderIds(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  const handleRecordDeferredPayment = async (orderId: number) => {
    try {
      setUpdatingOrderIds(prev => new Set(prev).add(orderId));
      await orderService.update(orderId, { payment_status: 'paid' } as any);
      setNotification({ type: 'success', message: t('adminOrders.paymentRecordedSuccess') });
      await loadOrders();
      await loadStats();
    } catch (err: any) {
      setNotification({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Erreur lors de l’enregistrement du paiement',
      });
    } finally {
      setUpdatingOrderIds(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  const handleUpdatePaymentStatus = async (orderId: number, paymentStatus: 'paid' | 'pending', successMessage: string) => {
    try {
      setUpdatingOrderIds(prev => new Set(prev).add(orderId));
      await orderService.update(orderId, { payment_status: paymentStatus } as any);
      setNotification({ type: 'success', message: successMessage });
      await loadOrders();
      await loadStats();
    } catch (err: any) {
      setNotification({
        type: 'error',
        message: err.response?.data?.error || err.message || t('adminOrders.paymentUpdateError'),
      });
    } finally {
      setUpdatingOrderIds(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  const getProfessionalActions = (order: Order) => {
    const currentStatus = order.order_status || '';
    const isUpdating = updatingOrderIds.has(order.id);
    const isCancelled = currentStatus === 'annulee';
    const isDelivered = currentStatus === 'livree';
    const canGenerateQuote = currentStatus !== 'devis' && !isCancelled;
    const canValidate = currentStatus === 'devis' && !isCancelled;
    const canCreateInvoice = ['validee', 'facture_emise', 'paiement_differe'].includes(currentStatus) && !isCancelled;
    const canMarkDelivered = !isDelivered && !isCancelled && ['facture_emise', 'paiement_differe', 'validee'].includes(currentStatus);
    const canRecordPayment = ['paiement_differe', 'livree', 'facture_emise'].includes(currentStatus) && order.payment_status !== 'paid' && !isCancelled;
    const canSetPaymentTotal = order.payment_status !== 'paid' && !isCancelled;
    const canSetPaymentDeferred = order.payment_status !== 'pending' && !isCancelled;
    const canCancel = !isCancelled;

    return [
      {
        key: 'quote',
        className: 'action-btn action-view',
        title: t('adminOrders.generateQuote'),
        ariaLabel: t('adminOrders.generateQuote'),
        icon: <DocumentTextIcon className="w-4 h-4" />,
        onClick: () => handleQuickStatusUpdate(order.id, 'devis', t('adminOrders.quoteGeneratedSuccess')),
        disabled: isUpdating || !canGenerateQuote,
      },
      {
        key: 'validate',
        className: 'action-btn action-edit',
        title: t('adminOrders.validate'),
        ariaLabel: t('adminOrders.validate'),
        icon: <CheckCircleIcon className="w-4 h-4" />,
        onClick: () => handleQuickStatusUpdate(order.id, 'validee', t('adminOrders.validateSuccess')),
        disabled: isUpdating || !canValidate,
      },
      {
        key: 'invoice',
        className: 'action-btn action-edit',
        title: t('adminOrders.createInvoice'),
        ariaLabel: t('adminOrders.createInvoice'),
        icon: <DocumentTextIcon className="w-4 h-4" />,
        onClick: async () => {
          await handleCreateInvoice(order.id);
          await handleQuickStatusUpdate(order.id, 'facture_emise', t('adminOrders.invoiceCreatedSuccess'));
        },
        disabled: isUpdating || !canCreateInvoice,
      },
      {
        key: 'deliver',
        className: 'action-btn action-view',
        title: t('adminOrders.markDelivered'),
        ariaLabel: t('adminOrders.markDelivered'),
        icon: <TruckIcon className="w-4 h-4" />,
        onClick: () => handleQuickStatusUpdate(order.id, 'livree', t('adminOrders.markDeliveredSuccess')),
        disabled: isUpdating || !canMarkDelivered,
      },
      {
        key: 'payment',
        className: 'action-btn action-edit',
        title: t('adminOrders.recordPayment'),
        ariaLabel: t('adminOrders.recordPayment'),
        icon: <BanknotesIcon className="w-4 h-4" />,
        onClick: () => handleRecordDeferredPayment(order.id),
        disabled: isUpdating || !canRecordPayment,
      },
      {
        key: 'payment-total',
        className: 'action-btn action-view',
        title: t('adminOrders.paymentTotal'),
        ariaLabel: t('adminOrders.paymentTotal'),
        icon: <CheckCircleIcon className="w-4 h-4" />,
        onClick: () => handleUpdatePaymentStatus(order.id, 'paid', t('adminOrders.paymentTotalSuccess')),
        disabled: isUpdating || !canSetPaymentTotal,
      },
      {
        key: 'payment-deferred',
        className: 'action-btn action-edit',
        title: t('adminOrders.paymentDeferred'),
        ariaLabel: t('adminOrders.paymentDeferred'),
        icon: <ClockIcon className="w-4 h-4" />,
        onClick: () => handleUpdatePaymentStatus(order.id, 'pending', t('adminOrders.paymentDeferredSuccess')),
        disabled: isUpdating || !canSetPaymentDeferred,
      },
      {
        key: 'cancel',
        className: 'action-btn action-delete',
        title: t('adminOrders.cancelOrder'),
        ariaLabel: t('adminOrders.cancelOrder'),
        icon: <XCircleIcon className="w-4 h-4" />,
        onClick: () => handleQuickStatusUpdate(order.id, 'annulee', t('adminOrders.cancelOrderSuccess')),
        disabled: isUpdating || !canCancel,
      },
    ];
  };

  const filteredOrders = orders.filter(order => {
    // Filtre par onglet actif (statut de commande) - seulement si aucun filtre de statut manuel n'est sélectionné
    if (!filterOrderStatus && activeTab !== 'all') {
      if (!isProfessionalOrdersPage && activeTab === 'delivered') {
        // Pour les commandes terminées, inclure 'completed' et 'delivered'
        if (order.order_status !== 'completed' && order.order_status !== 'delivered') {
          return false;
        }
      } else if (order.order_status !== activeTab) {
        return false;
      }
    }

    // Filtre par recherche (côté client car recherche textuelle)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        order.order_number.toLowerCase().includes(search) ||
        order.customer_name.toLowerCase().includes(search) ||
        order.customer_email.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    if (filterCustomerType && order.customer_type !== filterCustomerType) {
      return false;
    }

    // Les filtres de date, statut de paiement et statut de commande sont maintenant gérés côté serveur
    // On garde seulement le filtre de recherche textuelle et client_type côté client

    return true;
  });

  const professionalStatusCounts = {
    devis: orders.filter(o => o.order_status === 'devis').length,
    validee: orders.filter(o => o.order_status === 'validee').length,
    facture_emise: orders.filter(o => o.order_status === 'facture_emise').length,
    paiement_differe: orders.filter(o => o.order_status === 'paiement_differe').length,
    livree: orders.filter(o => o.order_status === 'livree').length,
    annulee: orders.filter(o => o.order_status === 'annulee').length,
  };

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);
  const manualSubtotal = manualOrderForm.items.reduce((sum, item) => sum + ((item.total || 0) as number), 0);
  const manualTaxAmount = (manualSubtotal * (Number(manualOrderForm.tax_rate) || 0)) / 100;
  const manualTotal = manualSubtotal + Number(manualOrderForm.shipping_cost || 0) + manualTaxAmount;

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, filterDateFrom, filterDateTo, filterPaymentStatus, filterOrderStatus, filterCustomerType]);

  return (
    <>

    {notification && (
              <Alert
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification(null)}
              />
            )}

            {/* Page Header */}
            <div className="page-header-section">
              <div className="page-header-left">
                <h2 className="page-main-title">
                  {isProfessionalOrdersPage ? t('adminOrders.professionalOrders') : t('adminOrders.orders')}
                </h2>
                <p className="page-subtitle">
                  {isProfessionalOrdersPage ? t('adminOrders.manageProfessionalOrders') : t('adminOrders.orderList')}
                </p>
              </div>
              <div className="page-header-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => setManualOrderModalOpen(true)}
                >
                  <PlusIcon className="btn-icon" />
                  {t('adminOrders.newManualOrder')}
                </button>
              </div>
            </div>

            {/* KPI Cards Row */}
            <div className="kpi-row">
              {isProfessionalOrdersPage ? (
                <>
                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <ShoppingCartIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#8C1843' }} />
                        <h3 className="kpi-title">{t('adminOrders.totalProfessionalOrders')}</h3>
                      </div>
                      <span className="kpi-index">#0</span>
                    </div>
                    <div className="kpi-value">{stats.total.toLocaleString('fr-FR')}</div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <DocumentTextIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#F59E0B' }} />
                        <h3 className="kpi-title">{t('adminOrders.devis')}</h3>
                      </div>
                      <span className="kpi-index">#1</span>
                    </div>
                    <div className="kpi-value">{professionalStatusCounts.devis.toLocaleString('fr-FR')}</div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <CheckCircleIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#3B82F6' }} />
                        <h3 className="kpi-title">{t('adminOrders.validee')}</h3>
                      </div>
                      <span className="kpi-index">#2</span>
                    </div>
                    <div className="kpi-value">{professionalStatusCounts.validee.toLocaleString('fr-FR')}</div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <DocumentTextIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#6366F1' }} />
                        <h3 className="kpi-title">{t('adminOrders.facture_emise')}</h3>
                      </div>
                      <span className="kpi-index">#3</span>
                    </div>
                    <div className="kpi-value">{professionalStatusCounts.facture_emise.toLocaleString('fr-FR')}</div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <ClockIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#8B5CF6' }} />
                        <h3 className="kpi-title">{t('adminOrders.paiement_differe')}</h3>
                      </div>
                      <span className="kpi-index">#4</span>
                    </div>
                    <div className="kpi-value">{professionalStatusCounts.paiement_differe.toLocaleString('fr-FR')}</div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <TruckIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#10B981' }} />
                        <h3 className="kpi-title">{t('adminOrders.livree')}</h3>
                      </div>
                      <span className="kpi-index">#5</span>
                    </div>
                    <div className="kpi-value">{professionalStatusCounts.livree.toLocaleString('fr-FR')}</div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <XCircleIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#EF4444' }} />
                        <h3 className="kpi-title">{t('adminOrders.annulee')}</h3>
                      </div>
                      <span className="kpi-index">#6</span>
                    </div>
                    <div className="kpi-value">{professionalStatusCounts.annulee.toLocaleString('fr-FR')}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <ShoppingCartIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#8C1843' }} />
                        <h3 className="kpi-title">{t('adminOrders.totalOrders')}</h3>
                      </div>
                      <span className="kpi-index">#1</span>
                    </div>
                    <div className="kpi-value">{stats.total.toLocaleString('fr-FR')}</div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <ClockIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#F59E0B' }} />
                        <h3 className="kpi-title">{t('adminOrders.newOrders')}</h3>
                      </div>
                      <span className="kpi-index">#2</span>
                    </div>
                    <div className="kpi-value">{stats.pending.toLocaleString('fr-FR')}</div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <ArrowPathIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#3B82F6' }} />
                        <h3 className="kpi-title">{t('adminOrders.ordersInProgress')}</h3>
                      </div>
                      <span className="kpi-index">#3</span>
                    </div>
                    <div className="kpi-value">{stats.inProgress.toLocaleString('fr-FR')}</div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <CheckCircleIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#10B981' }} />
                        <h3 className="kpi-title">{t('adminOrders.completedOrders')}</h3>
                      </div>
                      <span className="kpi-index">#4</span>
                    </div>
                    <div className="kpi-value">{stats.delivered.toLocaleString('fr-FR')}</div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-card-header">
                      <div className="kpi-header-content">
                        <XCircleIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#EF4444' }} />
                        <h3 className="kpi-title">{t('adminOrders.cancelledOrders')}</h3>
                      </div>
                      <span className="kpi-index">#5</span>
                    </div>
                    <div className="kpi-value">{stats.cancelled.toLocaleString('fr-FR')}</div>
                  </div>
                </>
              )}
            </div>

            {/* Orders Table Card */}
            <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">
                  {isProfessionalOrdersPage ? t('adminOrders.professionalOrders') : t('adminOrders.orders')} ({filteredOrders.length})
                </h3>
                <div className="flex gap-2 items-center">
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('adminOrders.resetFilters')}
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Tabs */}
              <div className="orders-tabs" style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
                {isProfessionalOrdersPage ? (
                  <>
                    {professionalStatusTabs.map((tab) => (
                      <button
                        key={tab.key}
                        className={`order-tab ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                      >
                        {tab.label}{' '}
                        <span className="tab-count">
                          (
                          {tab.key === 'all'
                            ? orders.length
                            : orders.filter(o => o.order_status === tab.key).length}
                          )
                        </span>
                      </button>
                    ))}
                  </>
                ) : (
                  <>
                    <button
                      className={`order-tab ${activeTab === 'all' ? 'active' : ''}`}
                      onClick={() => setActiveTab('all')}
                    >
                      {t('adminOrders.allOrders')} <span className="tab-count">({orders.length})</span>
                    </button>
                    <button
                      className={`order-tab ${activeTab === 'pending' ? 'active' : ''}`}
                      onClick={() => setActiveTab('pending')}
                    >
                      {t('adminOrders.newOrders')} <span className="tab-count">({stats.pending})</span>
                    </button>
                    <button
                      className={`order-tab ${activeTab === 'processing' ? 'active' : ''}`}
                      onClick={() => setActiveTab('processing')}
                    >
                      {t('adminOrders.processing')} <span className="tab-count">({orders.filter(o => o.order_status === 'processing').length})</span>
                    </button>
                    <button
                      className={`order-tab ${activeTab === 'shipped' ? 'active' : ''}`}
                      onClick={() => setActiveTab('shipped')}
                    >
                      {t('adminOrders.shipped')} <span className="tab-count">({orders.filter(o => o.order_status === 'shipped').length})</span>
                    </button>
                    <button
                      className={`order-tab ${activeTab === 'delivered' ? 'active' : ''}`}
                      onClick={() => setActiveTab('delivered')}
                    >
                      {t('adminOrders.completed')} <span className="tab-count">({stats.delivered})</span>
                    </button>
                    <button
                      className={`order-tab ${activeTab === 'cancelled' ? 'active' : ''}`}
                      onClick={() => setActiveTab('cancelled')}
                    >
                      {t('adminOrders.cancelled')} <span className="tab-count">({stats.cancelled})</span>
                    </button>
                  </>
                )}
              </div>

              {/* Search Bar & Filters */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminOrders.search')}</label>
                  <input
                    type="text"
                    placeholder={t('adminOrders.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminOrders.dateFrom')}</label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminOrders.dateTo')}</label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminOrders.paymentStatusFilter')}</label>
                  <select
                    value={filterPaymentStatus}
                    onChange={(e) => setFilterPaymentStatus(e.target.value)}
                    className="form-select"
                  >
                    <option value="">{t('adminOrders.all')}</option>
                    <option value="paid">{t('adminOrders.paid')}</option>
                    <option value="pending">{t('adminOrders.pending')}</option>
                    <option value="unpaid">{t('adminOrders.unpaid')}</option>
                  </select>
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminOrders.orderStatusFilter')}</label>
                  <select
                    value={filterOrderStatus}
                    onChange={(e) => setFilterOrderStatus(e.target.value)}
                    className="form-select"
                  >
                    <option value="">{t('adminOrders.allStatuses')}</option>
                    {isProfessionalOrdersPage ? (
                      <>
                        <option value="devis">{t('adminOrders.devis')}</option>
                        <option value="validee">{t('adminOrders.validee')}</option>
                        <option value="facture_emise">{t('adminOrders.facture_emise')}</option>
                        <option value="paiement_differe">{t('adminOrders.paiement_differe')}</option>
                        <option value="livree">{t('adminOrders.livree')}</option>
                        <option value="annulee">{t('adminOrders.annulee')}</option>
                      </>
                    ) : (
                      <>
                        <option value="pending">{t('adminOrders.pending')}</option>
                        <option value="processing">{t('adminOrders.processing')}</option>
                        <option value="shipped">{t('adminOrders.shipped')}</option>
                        <option value="delivered">{t('adminOrders.delivered')}</option>
                        <option value="completed">{t('adminOrders.completed')}</option>
                        <option value="cancelled">{t('adminOrders.cancelled')}</option>
                      </>
                    )}
                  </select>
                </div>
                {(filterDateFrom || filterDateTo || filterPaymentStatus || filterOrderStatus) && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setFilterDateFrom('');
                      setFilterDateTo('');
                      setFilterPaymentStatus('');
                      setFilterOrderStatus('');
                    }}
                  >
                    {t('adminOrders.reset')}
                  </button>
                )}
              </div>

              {/* Bulk Actions Bar */}
              {selectedOrders.length > 0 && (
                <div className="bulk-actions-bar" style={{ 
                  padding: '12px 16px', 
                  background: (updatingBulk || creatingBulkInvoices) ? '#fff3cd' : '#f5f5f5', 
                  borderRadius: '8px', 
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  border: (updatingBulk || creatingBulkInvoices) ? '1px solid #ffc107' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {updatingBulk ? (
                      <>
                        <ArrowPathIcon className="w-5 h-5 animate-spin" style={{ color: '#ffc107' }} />
                        <strong>{t('adminOrders.bulkUpdating', { count: selectedOrders.length })}</strong>
                      </>
                    ) : creatingBulkInvoices ? (
                      <>
                        <ArrowPathIcon className="w-5 h-5 animate-spin" style={{ color: '#ffc107' }} />
                        <strong>{t('adminOrders.bulkCreatingInvoices', { count: selectedOrders.length })}</strong>
                      </>
                    ) : (
                      <strong>{t('adminOrders.selectedCount', { count: selectedOrders.length })}</strong>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleBulkCreateInvoices}
                      disabled={updatingBulk || creatingBulkInvoices}
                      title={t('adminOrders.createInvoicesTitle')}
                    >
                      {creatingBulkInvoices ? t('adminOrders.creating') : t('adminOrders.createInvoices')}
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setBulkActionModalOpen(true)}
                      disabled={updatingBulk || creatingBulkInvoices}
                    >
                      {t('adminOrders.editStatus')}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setSelectedOrders([])}
                      disabled={updatingBulk || creatingBulkInvoices}
                    >
                      {t('adminOrders.cancelSelection')}
                    </button>
                  </div>
                </div>
              )}

              {error ? (
                <Alert type="error" message={error} />
              ) : (
                <ResponsiveTable
                columns={[
                  {
                    key: 'select',
                    label: (
                      <input
                        type="checkbox"
                    checked={selectedOrders.length === paginatedOrders.length && paginatedOrders.length > 0 && paginatedOrders.every(o => selectedOrders.includes(o.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOrders([...selectedOrders, ...paginatedOrders.map(o => o.id).filter(id => !selectedOrders.includes(id))]);
                      } else {
                        setSelectedOrders(selectedOrders.filter(id => !paginatedOrders.map(o => o.id).includes(id)));
                      }
                    }}
                        style={{ cursor: 'pointer' }}
                      />
                    ),
                    render: (order) => (
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOrders([...selectedOrders, order.id]);
                          } else {
                            setSelectedOrders(selectedOrders.filter(id => id !== order.id));
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    ),
                    mobileHidden: true,
                  },
                  {
                    key: 'index',
                    label: t('adminOrders.no'),
                    mobileHidden: true,
                    render: (order, index) => index + 1,
                  },
                  {
                    key: 'order_number',
                    label: t('adminOrders.orderNumber'),
                    render: (order) => <strong>{order.order_number}</strong>,
                  },
                  {
                    key: 'customer',
                    label: t('adminOrders.client'),
                    render: (order) => (
                      <div>
                        <div className="table-cell-name">{order.customer_name}</div>
                        <div className="text-xs text-body">{order.customer_email}</div>
                      </div>
                    ),
                  },
                  {
                    key: 'products',
                    label: t('adminOrders.productsCol'),
                    render: (order) => {
                      const items = parseOrderItems(order.items);
                      const firstProduct = items[0];
                      return (
                        <div className="product-cell">
                          <span>
                            <strong>{firstProduct?.product_name || t('adminOrders.product')}</strong>
                            {items.length > 1 && <span className="text-body"> {t('adminOrders.otherProducts', { count: items.length - 1 })}</span>}
                          </span>
                        </div>
                      );
                    },
                  },
                  {
                    key: 'customer_type',
                    label: t('adminOrders.customerType'),
                    render: (order) => (
                      <span>
                        {order.customer_type === 'professionnel' ? 'Professionnel' : 'Particulier'}
                      </span>
                    ),
                    mobileHidden: true,
                  },
                  {
                    key: 'date',
                    label: t('adminOrders.dateCol'),
                    mobileLabel: t('adminOrders.createdOnShort'),
                    render: (order) => <strong>{formatDate(order.created_at)}</strong>,
                  },
                  {
                    key: 'total',
                    label: t('adminOrders.priceCol'),
                    render: (order) => <strong>{formatPrice(order.total)}</strong>,
                  },
                  {
                    key: 'payment',
                    label: t('adminOrders.paymentCol'),
                    render: (order) => getPaymentBadge(order.payment_status),
                  },
                  {
                    key: 'status',
                    label: t('adminOrders.statusCol'),
                    render: (order) => getStatusBadge(order.order_status),
                  },
                  {
                    key: 'actions',
                    label: t('adminOrders.actions'),
                    mobileHidden: true,
                    render: (order) => (
                      <div className="product-actions">
                        {isProfessionalOrdersPage ? (
                          <>
                            <button
                              className="action-btn action-view"
                              onClick={() => setDetailModalOpen(order)}
                              title={t('adminOrders.viewDetails')}
                              aria-label={t('adminOrders.viewDetailsAria')}
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            {invoicesMap && invoicesMap[order.id] ? (
                              <button
                                className="action-btn action-view"
                                onClick={() => navigate(`/admin/factures/${invoicesMap[order.id].id}`)}
                                title={t('adminOrders.viewInvoice')}
                                aria-label={t('adminOrders.viewInvoice')}
                              >
                                <DocumentTextIcon className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                className="action-btn action-edit"
                                onClick={() => handleCreateInvoice(order.id)}
                                disabled={creatingInvoice === order.id}
                                title={t('adminOrders.createInvoice')}
                                aria-label={t('adminOrders.createInvoice')}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '32px', minHeight: '32px' }}
                              >
                                <PlusIcon className="w-4 h-4" style={{ display: 'block' }} />
                              </button>
                            )}
                            <button
                              className="action-btn action-edit"
                              onClick={() => handleOpenStatusModal(order.id, order.order_status)}
                              title={t('adminOrders.editStatus')}
                              aria-label={t('adminOrders.editStatusAria')}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                              </svg>
                            </button>
                            <button
                              className="action-btn action-delete"
                              onClick={() => handleDelete(order.id, order.order_number)}
                              title={t('adminOrders.delete')}
                              aria-label={t('adminOrders.deleteAria')}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                              </svg>
                            </button>
                            {getProfessionalActions(order).map((action) => (
                              <button
                                key={action.key}
                                className={action.className}
                                onClick={action.onClick}
                                title={action.title}
                                aria-label={action.ariaLabel}
                                disabled={action.disabled}
                              >
                                {action.icon}
                              </button>
                            ))}
                          </>
                        ) : (
                          <>
                        <button
                          className="action-btn action-view"
                          onClick={() => setDetailModalOpen(order)}
title={t('adminOrders.viewDetails')}
                              aria-label={t('adminOrders.viewDetailsAria')}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {invoicesMap && invoicesMap[order.id] ? (
                          <button
                            className="action-btn action-view"
                            onClick={() => navigate(`/admin/factures/${invoicesMap[order.id].id}`)}
                            title={t('adminOrders.viewInvoice')}
                            aria-label={t('adminOrders.viewInvoice')}
                          >
                            <DocumentTextIcon className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            className="action-btn action-edit"
                            onClick={() => handleCreateInvoice(order.id)}
                            disabled={creatingInvoice === order.id}
title={t('adminOrders.createInvoice')}
                                aria-label={t('adminOrders.createInvoice')}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '32px', minHeight: '32px' }}
                          >
                            <PlusIcon className="w-4 h-4" style={{ display: 'block' }} />
                          </button>
                        )}
                        <button
                          className="action-btn action-edit"
                          onClick={() => handleOpenStatusModal(order.id, order.order_status)}
title={t('adminOrders.editStatus')}
                              aria-label={t('adminOrders.editStatusAria')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                          </svg>
                        </button>
                        <button
                          className="action-btn action-delete"
                          onClick={() => handleDelete(order.id, order.order_number)}
title={t('adminOrders.delete')}
                              aria-label={t('adminOrders.deleteAria')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                          </svg>
                        </button>
                          </>
                        )}
                      </div>
                    ),
                  },
                ]}
                data={paginatedOrders}
                loading={loading}
                loadingMessage={t('adminOrders.loading')}
                emptyMessage={
                  searchTerm || activeTab !== 'all'
                    ? t('adminOrders.noFilters')
                    : t('adminOrders.empty')
                }
                mobileCardTitle={(order) => (
                  <div>
                    <div className="table-cell-name mb-1">{order.order_number}</div>
                    <div className="text-xs text-body">{order.customer_name}</div>
                  </div>
                )}
                mobileCardActions={(order) => (
                  <div className="product-actions" style={{ display: 'flex', gap: '8px' }}>
                    {isProfessionalOrdersPage ? (
                      <>
                        <button
                          className="action-btn action-view"
                          onClick={() => setDetailModalOpen(order)}
title={t('adminOrders.viewDetails')}
                              aria-label={t('adminOrders.viewDetailsAria')}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {invoicesMap && invoicesMap[order.id] ? (
                          <button
                            className="action-btn action-view"
                            onClick={() => navigate(`/admin/factures/${invoicesMap[order.id].id}`)}
                            title={t('adminOrders.viewInvoice')}
                            aria-label={t('adminOrders.viewInvoice')}
                          >
                            <DocumentTextIcon className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            className="action-btn action-edit"
                            onClick={() => handleCreateInvoice(order.id)}
                            disabled={creatingInvoice === order.id}
title={t('adminOrders.createInvoice')}
                                aria-label={t('adminOrders.createInvoice')}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '32px', minHeight: '32px' }}
                          >
                            <PlusIcon className="w-4 h-4" style={{ display: 'block' }} />
                          </button>
                        )}
                        <button
                          className="action-btn action-edit"
                          onClick={() => handleOpenStatusModal(order.id, order.order_status)}
title={t('adminOrders.editStatus')}
                              aria-label={t('adminOrders.editStatusAria')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                          </svg>
                        </button>
                        <button
                          className="action-btn action-delete"
                          onClick={() => handleDelete(order.id, order.order_number)}
title={t('adminOrders.delete')}
                              aria-label={t('adminOrders.deleteAria')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                          </svg>
                        </button>
                        {getProfessionalActions(order).map((action) => (
                          <button
                            key={action.key}
                            className={action.className}
                            onClick={action.onClick}
                            title={action.title}
                            aria-label={action.ariaLabel}
                            disabled={action.disabled}
                          >
                            {action.icon}
                          </button>
                        ))}
                      </>
                    ) : (
                      <>
                    <button
                      className="action-btn action-view"
                      onClick={() => setDetailModalOpen(order)}
title={t('adminOrders.viewDetails')}
                              aria-label={t('adminOrders.viewDetailsAria')}
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    {invoicesMap && invoicesMap[order.id] ? (
                      <button
                        className="action-btn action-view"
                        onClick={() => navigate(`/admin/factures/${invoicesMap[order.id].id}`)}
                        title={t('adminOrders.viewInvoice')}
                        aria-label={t('adminOrders.viewInvoice')}
                      >
                        <DocumentTextIcon className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        className="action-btn action-edit"
                        onClick={() => handleCreateInvoice(order.id)}
                        disabled={creatingInvoice === order.id}
title={t('adminOrders.createInvoice')}
                                aria-label={t('adminOrders.createInvoice')}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '32px', minHeight: '32px' }}
                      >
                        <PlusIcon className="w-4 h-4" style={{ display: 'block' }} />
                      </button>
                    )}
                    <button
                      className="action-btn action-edit"
                      onClick={() => handleOpenStatusModal(order.id, order.order_status)}
title={t('adminOrders.editStatus')}
                              aria-label={t('adminOrders.editStatusAria')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    </button>
                    <button
                      className="action-btn action-delete"
                      onClick={() => handleDelete(order.id, order.order_number)}
title={t('adminOrders.delete')}
                              aria-label={t('adminOrders.deleteAria')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                      </svg>
                    </button>
                      </>
                    )}
                  </div>
                )}
                tableClassName="products-table"
              />
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '24px',
                  padding: '16px',
                  borderTop: '1px solid #e0e0e0'
                }}>
                  <div className="pagination-info" style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminOrders.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredOrders.length), total: filteredOrders.length })}
                  </div>
                  <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('adminOrders.previous')}
                    </button>
                    <div className="pagination-pages" style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                              onClick={() => setCurrentPage(page)}
                              style={{ minWidth: '36px' }}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '0 4px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('adminOrders.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modal de création de commande manuelle */}
            {manualOrderModalOpen && (
              <div
                className="modal-overlay"
                onClick={() => !manualOrderSubmitting && setManualOrderModalOpen(false)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="manual-order-modal-title"
              >
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: '900px', width: '95%' }}
                >
                  <div className="modal-header">
                    <h3 id="manual-order-modal-title" className="modal-title">
                      {t('adminOrders.newManualOrder')}
                    </h3>
                    <button
                      className="modal-close"
                      onClick={() => !manualOrderSubmitting && setManualOrderModalOpen(false)}
                      aria-label={t('adminOrders.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    <input type="hidden" name="customer_type" value={forcedManualCustomerType} />

                    {forcedManualCustomerType === 'professionnel' && (
                      <div className="form-group">
                        <label className="form-label">{t('adminOrders.companyName')}</label>
                        <input
                          className={`form-input ${manualOrderErrors.company_name ? 'error' : ''}`}
                          value={manualOrderForm.company_name}
                          onChange={(e) => handleManualOrderFieldChange('company_name', e.target.value)}
                          placeholder={t('adminOrders.companyNamePlaceholder')}
                        />
                        {manualOrderErrors.company_name && (
                          <span className="error-message">{manualOrderErrors.company_name}</span>
                        )}
                      </div>
                    )}

                    <div className="manual-order-grid">
                      <div className="form-group">
                        <label className="form-label">{t('adminOrders.fullName')}</label>
                        <input
                          className={`form-input ${manualOrderErrors.customer_name ? 'error' : ''}`}
                          value={manualOrderForm.customer_name}
                          onChange={(e) => handleManualOrderFieldChange('customer_name', e.target.value)}
                          placeholder={t('adminOrders.fullNamePlaceholder')}
                        />
                        {manualOrderErrors.customer_name && (
                          <span className="error-message">{manualOrderErrors.customer_name}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('adminClients.email')}</label>
                        <input
                          type="email"
                          className={`form-input ${manualOrderErrors.customer_email ? 'error' : ''}`}
                          value={manualOrderForm.customer_email}
                          onChange={(e) => handleManualOrderFieldChange('customer_email', e.target.value)}
                          placeholder={t('adminOrders.emailPlaceholder')}
                        />
                        {manualOrderErrors.customer_email && (
                          <span className="error-message">{manualOrderErrors.customer_email}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('adminClients.phone')} *</label>
                        <input
                          className={`form-input ${manualOrderErrors.customer_phone ? 'error' : ''}`}
                          value={manualOrderForm.customer_phone}
                          onChange={(e) => handleManualOrderFieldChange('customer_phone', e.target.value)}
                          placeholder={t('adminOrders.phonePlaceholder')}
                        />
                        {manualOrderErrors.customer_phone && (
                          <span className="error-message">{manualOrderErrors.customer_phone}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('adminClients.address')} *</label>
                        <input
                          className={`form-input ${manualOrderErrors.customer_address ? 'error' : ''}`}
                          value={manualOrderForm.customer_address}
                          onChange={(e) => handleManualOrderFieldChange('customer_address', e.target.value)}
                          placeholder={t('adminOrders.addressPlaceholder')}
                        />
                        {manualOrderErrors.customer_address && (
                          <span className="error-message">{manualOrderErrors.customer_address}</span>
                        )}
                      </div>
                      <div className="form-group" style={{ position: 'relative' }}>
                        <label className="form-label">{t('adminClients.city')} *</label>
                        <input
                          className={`form-input ${manualOrderErrors.customer_city ? 'error' : ''}`}
                          value={manualOrderForm.customer_city}
                          onChange={(e) => handleManualOrderFieldChange('customer_city', e.target.value)}
                          placeholder={t('adminOrders.cityPlaceholder')}
                        />
                        {(cityLookupStatus !== 'idle' ||
                          cityNameLookupStatus !== 'idle' ||
                          [...citySuggestions, ...cityNameSuggestions].length > 0) && (
                          <div className="suggestions-dropdown">
                            {(cityLookupStatus === 'loading' || cityNameLookupStatus === 'loading') && (
                              <div className="suggestion-message">{t('adminOrders.searchingCities')}</div>
                            )}
                            {(cityLookupStatus === 'not_found' || cityNameLookupStatus === 'not_found') &&
                              [...citySuggestions, ...cityNameSuggestions].length === 0 && (
                              <div className="suggestion-message">{t('adminOrders.noCityFound')}</div>
                            )}
                            {Array.from(new Set([...citySuggestions, ...cityNameSuggestions])).map((city) => (
                              <button
                                key={city}
                                type="button"
                                className="suggestion-item"
                                onClick={() => handleSelectCity(city)}
                              >
                                {city}
                              </button>
                            ))}
                          </div>
                        )}
                        {manualOrderErrors.customer_city && (
                          <span className="error-message">{manualOrderErrors.customer_city}</span>
                        )}
                      </div>
                      <div className="form-group" style={{ position: 'relative' }}>
                        <label className="form-label">{t('adminClients.postalCode')} *</label>
                        <input
                          className={`form-input ${manualOrderErrors.customer_postal_code ? 'error' : ''}`}
                          value={manualOrderForm.customer_postal_code}
                          onChange={(e) => handleManualOrderFieldChange('customer_postal_code', e.target.value)}
                          placeholder={t('adminOrders.postalPlaceholder')}
                        />
                        {(postalLookupStatus !== 'idle' || postalSuggestions.length > 0) && (
                          <div className="suggestions-dropdown">
                            {postalLookupStatus === 'loading' && (
                              <div className="suggestion-message">{t('adminOrders.searchingPostalCodes')}</div>
                            )}
                            {postalLookupStatus === 'not_found' && postalSuggestions.length === 0 && (
                              <div className="suggestion-message">{t('adminOrders.noPostalCodeFound')}</div>
                            )}
                            {postalSuggestions.map((postalCode) => (
                              <button
                                key={postalCode}
                                type="button"
                                className="suggestion-item"
                                onClick={() => handleSelectPostalCode(postalCode)}
                              >
                                {postalCode}
                              </button>
                            ))}
                          </div>
                        )}
                        {manualOrderErrors.customer_postal_code && (
                          <span className="error-message">{manualOrderErrors.customer_postal_code}</span>
                        )}
                      </div>
                    </div>

                    <div className="manual-order-items">
                      <div className="manual-order-items-header">
                        <h4>{t('adminOrders.items')}</h4>
                        <button className="btn btn-secondary btn-sm" type="button" onClick={addManualOrderItem}>
                          <PlusIcon className="btn-icon" />
                          {t('adminOrders.addItem')}
                        </button>
                      </div>
                      {manualProductsError && (
                        <div className="error-message">{manualProductsError}</div>
                      )}
                      {manualOrderForm.items.map((item, index) => (
                        <div className="manual-order-item-row" key={`manual-item-${index}`}>
                          <div className="form-group">
                            <label className="form-label">{t('adminOrders.productLabel')}</label>
                            <select
                              className={`form-select ${manualOrderErrors[`item_${index}_product_name`] ? 'error' : ''}`}
                              value={item.product_id || 0}
                              onChange={(e) => handleManualProductSelect(index, Number(e.target.value))}
                              disabled={manualProductsLoading}
                            >
                              <option value={0}>
                                {manualProductsLoading ? t('adminOrders.loadingProducts') : t('adminOrders.selectProduct')}
                              </option>
                              {manualProducts.map((product) => (
                                <option key={product.id} value={product.id}>
                                  {product.name}
                                </option>
                              ))}
                            </select>
                            {manualOrderErrors[`item_${index}_product_name`] && (
                              <span className="error-message">{manualOrderErrors[`item_${index}_product_name`]}</span>
                            )}
                          </div>
                          <div className="form-group">
                            <label className="form-label">{t('adminOrders.quantityLabel')}</label>
                            <input
                              type="number"
                              min={1}
                              className={`form-input ${manualOrderErrors[`item_${index}_quantity`] ? 'error' : ''}`}
                              value={item.quantity}
                              onChange={(e) => updateManualOrderItem(index, 'quantity', e.target.value)}
                            />
                            {manualOrderErrors[`item_${index}_quantity`] && (
                              <span className="error-message">{manualOrderErrors[`item_${index}_quantity`]}</span>
                            )}
                          </div>
                          <div className="form-group">
                            <label className="form-label">{t('adminOrders.unitPriceEur')}</label>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              className={`form-input ${manualOrderErrors[`item_${index}_price`] ? 'error' : ''}`}
                              value={item.price}
                              onChange={(e) => updateManualOrderItem(index, 'price', e.target.value)}
                            />
                            {manualOrderErrors[`item_${index}_price`] && (
                              <span className="error-message">{manualOrderErrors[`item_${index}_price`]}</span>
                            )}
                          </div>
                          <div className="form-group">
                            <label className="form-label">{t('adminOrders.totalLabel')}</label>
                            <input className="form-input" value={formatPrice(item.total || 0)} disabled />
                          </div>
                          <button
                            className="btn btn-secondary btn-sm"
                            type="button"
                            onClick={() => removeManualOrderItem(index)}
                            disabled={manualOrderForm.items.length === 1}
                          >
                            {t('adminOrders.remove')}
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="manual-order-summary">
                      <div className="form-group">
                        <label className="form-label">{t('adminOrders.shippingCost')}</label>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          className="form-input"
                          value={manualOrderForm.shipping_cost}
                          onChange={(e) => handleManualOrderFieldChange('shipping_cost', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('adminOrders.taxOptional')}</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step="0.1"
                          className="form-input"
                          value={manualOrderForm.tax_rate}
                          onChange={(e) => handleManualOrderFieldChange('tax_rate', parseFloat(e.target.value) || 0)}
                          placeholder={t('adminOrders.zero')}
                        />
                      </div>
                      <div className="manual-order-totals">
                        <div className="manual-order-total-row">
                          <span>{t('adminOrders.subtotal')}</span>
                          <strong>{formatPrice(manualSubtotal)}</strong>
                        </div>
                        {manualTaxAmount > 0 && (
                          <div className="manual-order-total-row">
                            <span>{t('adminOrders.tax', { rate: manualOrderForm.tax_rate })}</span>
                            <strong>{formatPrice(manualTaxAmount)}</strong>
                          </div>
                        )}
                        <div className="manual-order-total-row">
                          <span>{t('adminOrders.total')}</span>
                          <strong>{formatPrice(manualTotal)}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        if (!manualOrderSubmitting) {
                          setManualOrderModalOpen(false);
                        }
                      }}
                    >
                      {t('adminOrders.cancel')}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleCreateManualOrder}
                      disabled={manualOrderSubmitting}
                    >
                      {manualOrderSubmitting ? t('adminOrders.saving') : t('adminOrders.save')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de modification groupée */}
            {bulkActionModalOpen && (
              <div 
                className="modal-overlay" 
                onClick={() => setBulkActionModalOpen(false)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="bulk-modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 id="bulk-modal-title" className="modal-title">
                      {t('adminOrders.editStatusOf', { count: selectedOrders.length })}
                    </h3>
                    <button 
                      className="modal-close"
                      onClick={() => setBulkActionModalOpen(false)}
                      aria-label={t('adminOrders.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="form-group">
                      <label className="form-label">{t('adminOrders.newStatus')}</label>
                      <select
                        className="form-select"
                        value={bulkSelectedStatus}
                        onChange={(e) => setBulkSelectedStatus(e.target.value)}
                        aria-label={t('adminOrders.selectStatus')}
                      >
                        <option value="">{t('adminOrders.selectStatus')}</option>
                        {isProfessionalOrdersPage ? (
                          <>
                            <option value="devis">{t('adminOrders.devis')}</option>
                            <option value="validee">{t('adminOrders.validee')}</option>
                            <option value="facture_emise">{t('adminOrders.facture_emise')}</option>
                            <option value="paiement_differe">{t('adminOrders.paiement_differe')}</option>
                            <option value="livree">{t('adminOrders.livree')}</option>
                            <option value="annulee">{t('adminOrders.annulee')}</option>
                          </>
                        ) : (
                          <>
                            <option value="pending">{t('adminOrders.pending')}</option>
                            <option value="processing">{t('adminOrders.processing')}</option>
                            <option value="shipped">{t('adminOrders.shipped')}</option>
                            <option value="delivered">{t('adminOrders.delivered')}</option>
                            <option value="cancelled">{t('adminOrders.cancelled')}</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setBulkActionModalOpen(false);
                        setBulkSelectedStatus('');
                      }}
                      disabled={updatingBulk}
                    >
                      {t('adminOrders.cancel')}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleBulkUpdateStatus}
                      disabled={!bulkSelectedStatus || updatingBulk}
                    >
                      {updatingBulk ? t('adminOrders.applying') : t('adminOrders.apply')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de détail de commande */}
            {detailModalOpen && (
              <div 
                className="modal-overlay" 
                onClick={() => setDetailModalOpen(null)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="detail-modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
                  <div className="modal-header">
                    <h3 id="detail-modal-title" className="modal-title">
                      {t('adminOrders.orderDetails', { number: detailModalOpen.order_number })}
                    </h3>
                    <button 
                      className="modal-close"
                      onClick={() => setDetailModalOpen(null)}
                      aria-label={t('adminOrders.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    {(() => {
                      const order = detailModalOpen;
                      const items = parseOrderItems(order.items);
                      const accessState = orderAccessMap[order.id];
                      const accessData = accessState?.data;
                      
                      return (
    <>

                        <div className="printable-order" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                          {/* Informations client */}
                          <div>
                            <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                              {t('adminOrders.clientInfo')}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                              <div>
                                <strong>{t('adminOrders.nameLabel')}</strong> {order.customer_name}
                              </div>
                              <div>
                                <strong>{t('adminOrders.emailLabel')}</strong> {order.customer_email}
                              </div>
                              <div>
                                <strong>{t('adminOrders.customerType')}:</strong>{' '}
                                {order.customer_type === 'professionnel' ? t('adminOrders.professional') : t('adminOrders.particulier')}
                              </div>
                              {order.company_name && (
                                <div>
                                  <strong>{t('adminOrders.company')}</strong> {order.company_name}
                                </div>
                              )}
                              <div>
                                <strong>{t('adminOrders.phoneLabel')}</strong> {order.customer_phone || t('adminOrders.na')}
                              </div>
                              <div>
                                <strong>{t('adminOrders.cityLabel')}</strong> {order.customer_city || t('adminOrders.na')}
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <strong>{t('adminOrders.addressLabel')}</strong> {order.customer_address || t('adminOrders.na')}
                              </div>
                              {order.customer_postal_code && (
                                <div>
                                  <strong>{t('adminOrders.postalCodeLabel')}</strong> {order.customer_postal_code}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Produits */}
                          <div>
                            <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                              {t('adminOrders.products')} ({items.length})
                            </h4>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ background: '#f5f5f5' }}>
                                  <tr>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('adminOrders.product')}</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>{t('adminOrders.quantity')}</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>{t('adminOrders.unitPrice')}</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>{t('adminOrders.total')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((item: any, index: number) => (
                                    <tr key={index} style={{ borderTop: '1px solid #e0e0e0' }}>
                                      <td style={{ padding: '12px' }}>{item.product_name || t('adminOrders.product')}</td>
                                      <td style={{ padding: '12px', textAlign: 'center' }}>{item.quantity || 1}</td>
                                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatPrice(item.price || 0)}</td>
                                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>{formatPrice(item.total || 0)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Totaux */}
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <div style={{ width: '300px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{t('adminOrders.subtotal')}:</span>
                                <strong>{formatPrice(order.subtotal || 0)}</strong>
                              </div>
                              {order.shipping_cost > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span>{t('adminOrders.shippingCostLabel')}</span>
                                  <strong>{formatPrice(order.shipping_cost)}</strong>
                                </div>
                              )}
                              {order.tax_amount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span>{t('adminOrders.taxLabel', { rate: order.tax_rate || 0 })}</span>
                                  <strong>{formatPrice(order.tax_amount)}</strong>
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '2px solid #333', fontSize: '18px', fontWeight: '700' }}>
                                <span>{t('adminOrders.totalLabelDetail')}</span>
                                <strong>{formatPrice(order.total)}</strong>
                              </div>
                            </div>
                          </div>

                          {/* QR code de vérification */}
                          <div>
                            <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                              {t('adminOrders.qrVerification')}
                            </h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                              <div style={{ minWidth: '140px' }}>
                                {accessState?.loading ? (
                                  <div style={{ fontSize: '14px', color: '#666' }}>{t('adminOrders.generating')}</div>
                                ) : accessData?.qr_code_data_url ? (
                                  <img
                                    src={accessData.qr_code_data_url}
                                    alt={t('adminOrders.qrVerification')}
                                    style={{ width: '140px', height: '140px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', background: '#fff' }}
                                  />
                                ) : (
                                  <div style={{ fontSize: '14px', color: '#666' }}>{t('adminOrders.noQrCode')}</div>
                                )}
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' }}>
                                <button
                                  className="btn btn-primary btn-sm"
                                  type="button"
                                  onClick={() => handleGenerateAccessToken(order.id)}
                                  disabled={accessState?.loading}
                                >
                                  {accessData ? t('adminOrders.regenerateQr') : t('adminOrders.generateQr')}
                                </button>

                                {accessData?.access_url && (
                                  <a href={accessData.access_url} target="_blank" rel="noreferrer" style={{ fontSize: '13px', color: '#8C1843' }}>
                                    {t('adminOrders.openSecureLink')}
                                  </a>
                                )}

                                {accessData?.expires_at && (
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {t('adminOrders.expiresOn')} {formatDate(accessData.expires_at)}
                                  </div>
                                )}

                                {accessState?.error && (
                                  <div style={{ fontSize: '12px', color: '#f44336' }}>{accessState.error}</div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Résumé impression */}
                          <div className="print-only" style={{ display: 'none' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontSize: '18px', fontWeight: 700 }}>{t('adminOrders.orderFormTitle')}</div>
                                <div style={{ fontSize: '13px', color: '#555' }}>{t('adminOrders.orderLabel')} {order.order_number}</div>
                              </div>
                              {accessData?.qr_code_data_url && (
                                <img
                                  src={accessData.qr_code_data_url}
                                  alt="QR code de vérification"
                                  style={{ width: '110px', height: '110px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', background: '#fff' }}
                                />
                              )}
                            </div>
                          </div>

                          {/* Statuts et dates */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                            <div>
                              <strong>{t('adminOrders.orderStatusLabel')}</strong>
                              <div style={{ marginTop: '8px' }}>
                                {getStatusBadge(order.order_status)}
                              </div>
                            </div>
                            <div>
                              <strong>{t('adminOrders.paymentStatusLabelDetail')}</strong>
                              <div style={{ marginTop: '8px' }}>
                                {getPaymentBadge(order.payment_status)}
                              </div>
                            </div>
                            <div>
                              <strong>{t('adminOrders.createdAtLabel')}</strong>
                              <div style={{ marginTop: '4px', color: '#666' }}>
                                {formatDate(order.created_at)}
                              </div>
                            </div>
                            {order.updated_at && order.updated_at !== order.created_at && (
                              <div>
                                <strong>{t('adminOrders.lastModifiedLabel')}</strong>
                                <div style={{ marginTop: '4px', color: '#666' }}>
                                  {formatDate(order.updated_at)}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      
    </>
  );
                    })()}
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setDetailModalOpen(null)}
                    >
                      {t('adminOrders.close')}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handlePrintOrder(detailModalOpen, parseOrderItems(detailModalOpen.items))}
                      disabled={orderAccessMap[detailModalOpen.id]?.loading}
                    >
                      {t('adminOrders.printOrder')}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        handleViewOrCreateInvoice(detailModalOpen.id);
                        setDetailModalOpen(null);
                      }}
                      disabled={creatingInvoice === detailModalOpen.id}
                    >
                      {invoicesMap[detailModalOpen.id] ? t('adminOrders.viewInvoiceBtn') : t('adminOrders.createAndViewInvoice')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de changement de statut */}
            {statusModalOpen && (
              <div 
                className="modal-overlay" 
                onClick={handleCloseStatusModal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="status-modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 id="status-modal-title" className="modal-title">{t('adminOrders.editOrderStatus')}</h3>
                    <button 
                      className="modal-close"
                      onClick={handleCloseStatusModal}
                      aria-label={t('adminOrders.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="form-group">
                      <label className="form-label">{t('adminOrders.newStatus')}</label>
                      <select
                        className="form-select"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        aria-label={t('adminOrders.selectStatus')}
                      >
                        {isProfessionalOrdersPage ? (
                          <>
                            <option value="devis">{t('adminOrders.devis')}</option>
                            <option value="validee">{t('adminOrders.validee')}</option>
                            <option value="facture_emise">{t('adminOrders.facture_emise')}</option>
                            <option value="paiement_differe">{t('adminOrders.paiement_differe')}</option>
                            <option value="livree">{t('adminOrders.livree')}</option>
                            <option value="annulee">{t('adminOrders.annulee')}</option>
                          </>
                        ) : (
                          <>
                            <option value="pending">{t('adminOrders.pending')}</option>
                            <option value="processing">{t('adminOrders.processing')}</option>
                            <option value="shipped">{t('adminOrders.shipped')}</option>
                            <option value="delivered">{t('adminOrders.delivered')}</option>
                            <option value="cancelled">{t('adminOrders.cancelled')}</option>
                          </>
                        )}
                      </select>
                      <div className="form-help-text">
                        {t('adminOrders.currentStatus')} <strong>
                          {statusLabelMap[statusModalOpen.currentStatus] || statusModalOpen.currentStatus}
                        </strong>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button 
                      className="btn btn-secondary"
                      onClick={handleCloseStatusModal}
                      disabled={updatingStatus === statusModalOpen.orderId}
                    >
                      {t('adminOrders.cancel')}
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={handleUpdateStatus}
                      disabled={updatingStatus === statusModalOpen.orderId || selectedStatus === statusModalOpen.currentStatus}
                    >
                      {updatingStatus === statusModalOpen.orderId ? t('adminOrders.applying') : t('adminOrders.save')}
                    </button>
                  </div>
                </div>
              </div>
            )}
  
    </>
  );
};
